import { findWeekIdsInRange, getFirstDate, getLastDate } from "utils/Date";
import moment from "moment-timezone";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { FcWrap, CalendarNavBar, SHELL_TIMEGRID_PROPS, useNowIndicatorOverlay } from "pages/Calendar/calendarShell";
import queryKeys from "common/queryKey";
import { useScrollFetch } from "hooks/useScrollFetch";
import { useFetch } from "hooks/useFetch";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";


const EngagementWeekCalendar = ({ weeksThisMonth, viewType, setViewType }) => {
	const sDate = getFirstDate(moment());
	const eDate = getLastDate(moment());
	const userInfo = useSelector(getUserInfo);
	const userList = useSelector(getUserList);
	const currentUser = useMemo(() => {
		return userList.find((user) => user.userid === userInfo.userid);
	}, [userList, userInfo.userid]);

	const weekList = findWeekIdsInRange([sDate, eDate]);
	const calendarRef = useRef(null);
	const fcWrapRef = useRef(null);
	const [viewTitle, setViewTitle] = useState("");
	const [currentView, setCurrentView] = useState(viewType || "timeGridWeek");
	useNowIndicatorOverlay(fcWrapRef, [viewTitle, currentView]);
	const [payload, setPayload] = useState({
		startDate: new Date(sDate),
		endDate: new Date(eDate),
		employeeIds: [],
		engagementIds: [],
		weekId: weekList,
		managingOffice: currentUser?.managingoffice?.[0]
	});

	const { key: resourceKey, url: resourceUrl } =
		queryKeys.getResourePlanningList(payload?.weekId?.join(","));

	const {
		data: resourceDataRes,
		isLoading: isResourceLoading,
		refetch,
	} = useFetch(
		resourceKey,
		resourceUrl,
		{
			enabled: payload?.weekId?.length > 0,
		},
		payload
	);

	const myResourceData = useMemo(() => {
		return (
			resourceDataRes?.response?.response?.filter(
				(res) => res.userId === userInfo.userid
			) || []
		);
	}, [resourceDataRes, userInfo.userid]);

	const events = useMemo(() => {
		// Step 1: expand each resource booking into one entry per working day (skip weekends)
		const bookedDates = myResourceData.reduce((acc, res) => {
			const start = moment(res.actualBookedStartDate, "YYYY-MM-DD");
			const end   = moment(res.actualBookedEndDate,   "YYYY-MM-DD");
			if (!start.isValid() || !end.isValid()) {
				acc.push({ date: moment().format("YYYY-MM-DD"), ...res });
				return acc;
			}
			let cur = start.clone();
			while (cur.isBefore(end) || cur.isSame(end, "day")) {
				if (cur.day() !== 0 && cur.day() !== 6) {
					acc.push({ date: cur.format("YYYY-MM-DD"), ...res });
				}
				cur.add(1, "day");
			}
			return acc;
		}, []);

		// Step 2: group by date
		const byDate = {};
		bookedDates.forEach((e) => {
			if (!byDate[e.date]) byDate[e.date] = [];
			byDate[e.date].push(e);
		});

		// Step 3: calculate daily hours exactly like MyPlanner
		const calcDaily = (res) => {
			const personDay   = parseFloat(res.personDay) || 0;
			const workingDays = parseInt(res.bookedWorkingDays) || 0;
			if (personDay <= 0 || workingDays <= 0) return 8;
			return Math.min((personDay * 8) / workingDays, 8);
		};

		// Step 4: build one FC event per entry, starting at 08:00 AM
		const out = [];
		Object.entries(byDate).forEach(([date, entries]) => {
			entries.forEach((e, i) => {
				const dh    = calcDaily(e);
				const start = moment(date).set({ hour: 8, minute: 0, second: 0 }).toISOString();
				const end   = moment(date).set({ hour: 8, minute: 0, second: 0 }).add(dh, "hours").toISOString();
				out.push({
					id:              `matter-${date}-${e.resourceId || ""}-${i}`,
					title:           e.matterName || e.employeeName || "Matter",
					start,
					end,
					backgroundColor: "#DCFCE7",
					borderColor:     "#10B981",
					textColor:       "#1F243B",
					extendedProps:   { _type: "matter", _raw: e, _dailyHours: dh, ...e },
				});
			});
		});
		return out;
	}, [myResourceData]);

	const { getAllClientsList } = queryKeys;

	const { key, url } = getAllClientsList;

	const {
		data: clientsDataRes,
		fetchNextPage,
		isLoading: loading,
		hasNextPage,
	} = useScrollFetch(key, {
		endPoint: url,
		queryParam: "LastEvaluatedKey",
	});

	const clients = useMemo(() => {
		return clientsDataRes?.pages?.reduce((acc, page) => {
			if (page?.response?.data === undefined) return acc;
			return [...acc, ...page?.response?.data];
		}, []);
	}, [clientsDataRes]);

	const clientOptions = clients
		?.filter((client) => client?.bdo_id !== "NULL")
		?.map((client) => ({
			label: client?.clienttype === "company" ? client?.companyregisteredname : client?.clientname,
			value: client?.bdo_id,
		}));

	const getEventColor = (status) => {
		switch (status) {
			case "Un Assigned":
				return "#FBE2CD"; // Orange
			case "Assigned":
				return "#DCFCE7"; // Light Blue
			case 6:
				return "#FFCCCB"; // Light Red
			default:
				return "#ADD8E6"; // Default to white
		}
	};

	const getEventBorderColor = (status) => {
		switch (status) {
			case "Un Assigned":
				return "#F18D38";
			case 6:
				return "#38CDF1";
			case "Assigned":
				return "#10B981";
			default:
				return "#F18D38";
		}
	};

	const textStyle = {
		whiteSpace: "nowrap", // Prevent wrapping
		overflow: "hidden", // Hide overflow
		textOverflow: "ellipsis", // Show ellipsis for overflow
		
	};
	const textStyleBold = {
		whiteSpace: "nowrap", // Prevent wrapping
		overflow: "hidden", // Hide overflow
		textOverflow: "ellipsis", // Show ellipsis for overflow
		fontWeight: "600"
	};

	const handleDatesSet = (arg) => {
		setViewTitle(arg.view.title);
		setCurrentView(arg.view.type);
		setViewType?.(arg.view.type === "dayGridMonth" ? "dayGridMonth" : "timeGridWeek");
		const sDate = getFirstDate(moment(arg.start));
		const eDate = getLastDate(moment(arg.end));

		const weeks = findWeekIdsInRange([sDate, eDate]);

		const formattedStart = moment(arg.start).format("YYYY-MM-DD");
		const formattedEnd = moment(arg.end).format("YYYY-MM-DD");

		setPayload((prev) => {
			const isSame =
				JSON.stringify(prev.weekId) === JSON.stringify(weeks) &&
				prev.startDate === formattedStart &&
				prev.endDate === formattedEnd;

			if (isSame) return prev;

			return {
				...prev,
				startDate: formattedStart,
				endDate: formattedEnd,
				weekId: weeks,
				managingOffice: currentUser?.managingoffice?.[0],
			};
		});
	};

	return (
		<>
			{/* <Spin spinning={isResourceLoading}>
				<FullCalendar
					plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
					height="75vh"
					rerenderDelay={300}
					contentHeight="auto"
					initialView="timeGridWeek" // Set the initial view to week
					events={events} // Use your events array
					datesSet={handleDatesSet}
					headerToolbar={{
						left: "prev,next", // Navigation buttons
						center: "title", // Title in the center
						right: "timeGridWeek,dayGridMonth", // Week and Month views on the right
					}}
					editable={false}
					selectable={true}
					allDaySlot={false} // Disable the all-day slot
					eventOverlap={true} // Allow events to overlap
					stickyHeaderDates={true}
					slotEventOverlap={true} // Prevent events from stacking in the same slot
					eventLimit={3} // Limit the number of events shown in a slot
					slotMinTime="08:00:00"
					slotMaxTime="18:00:00"
					eventLimitClick={"popover"}
					eventContent={(info) => {
						const backgroundColor = getEventColor(
							info.event.extendedProps.status
						);
						const borderColor = getEventBorderColor(
							info.event.extendedProps.status
						);

						const startTime = info.event.start
							? moment(info.event.start).format("hh:mm A")
							: "N/A";
						const endTime = info.event.end
							? moment(info.event.end).format("hh:mm A")
							: "N/A";

						const clientName = clientOptions?.find(
							//? will give result only after submitting Engagement
							(client) => client?.value === info.event.extendedProps?.clientId
						)?.label;

						return (
							<div
								style={{
									padding: "5px",
									background: backgroundColor,
									border: "none",
									outline: "none",
									color: "#000",
									border: `1px solid ${borderColor}`,
									height: "auto",
									minWidth: "auto",
									width: "100%", // Full width
									boxSizing: "border-box", // Ensure padding is included in width
									display: "flex",
									flexDirection: "column",
								}}
							>
								<>
									<span style={textStyle}>{info?.event?.title !== "undefined" ? info?.event?.title : ""}</span>
									<span style={textStyle}>Matter:</span>
									<span style={textStyle}>
										{info.event.extendedProps?.matterName ?? "-"}
									</span>
									<span style={textStyle}>Client:</span>
									<span style={textStyle}> {clientName ?? "-"}</span>
									<p style={textStyle}>
										{startTime} - {endTime}
									</p>
								</>
							</div>
						);
					}}
				/>
			</Spin> */}
			<FcWrap style={{ position: "relative" }}>
				{isResourceLoading && (
					<div
						style={{
							position: "absolute",
							top: 10,
							right: 10,
							zIndex: 999,
						}}
					>
						<Spin />
					</div>
				)}

				<CalendarNavBar
					calendarRef={calendarRef}
					title={viewTitle}
					view={currentView}
					onViewChange={setCurrentView}
				/>

				<FullCalendar
					ref={calendarRef}
					plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
					{...SHELL_TIMEGRID_PROPS}
					rerenderDelay={300}
					initialView={viewType || "timeGridWeek"} // Set the initial view from shared state
					events={events} // Use your events array
					datesSet={handleDatesSet}
					headerToolbar={false}
					editable={false}
					selectable={true}
					allDaySlot={false} // Disable the all-day slot
					eventOverlap={true} // Allow events to overlap
					stickyHeaderDates={true}
					slotEventOverlap={true} // Prevent events from stacking in the same slot
					eventLimit={3} // Limit the number of events shown in a slot
					eventLimitClick={"popover"}
					eventDidMount={(info) => {
					const borderColor = info.event.borderColor || "#10B981";
					const bgColor     = info.event.backgroundColor || "#DCFCE7";
					const outerEl = info.el.closest(".fc-timegrid-event") || info.el.parentElement;
					if (outerEl) {
						outerEl.style.border       = "none";
						outerEl.style.borderLeft   = `3px solid ${borderColor}`;
						outerEl.style.borderRadius = "6px";
						outerEl.style.background   = bgColor;
						outerEl.style.overflow     = "hidden";
						outerEl.style.boxShadow    = "none";
					}
				}}
				eventContent={(info) => {
					const raw        = info.event.extendedProps?._raw || info.event.extendedProps;
					const dailyHours = info.event.extendedProps?._dailyHours;
					const durationMins = info.event.start && info.event.end
						? moment(info.event.end).diff(moment(info.event.start), "minutes") : 0;
					const isCompact = durationMins < 60;

					return (
						<div
							style={{
								padding: "3px 6px",
								background: "transparent",
								color: "#1F243B",
								height: "100%",
								width: "100%",
								boxSizing: "border-box",
								overflow: "hidden",
								display: "flex",
								flexDirection: "column",
								gap: 2,
								cursor: "pointer",
							}}
						>
							<span style={{ fontWeight: 600, fontSize: 14, lineHeight: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
								{info.event.title}
							</span>
							{!isCompact && info.event.start && info.event.end && (
								<span style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap" }}>
									{moment(info.event.start).format("hh:mm A")} – {moment(info.event.end).format("hh:mm A")}
								</span>
							)}
							{!isCompact && dailyHours != null && (
								<span style={{ fontSize: 12, fontWeight: 600, opacity: 0.9, whiteSpace: "nowrap" }}>
									{Number(dailyHours).toFixed(2)} Hrs/day
								</span>
							)}
						</div>
					);
				}}
				/>
			</FcWrap>
		</>
	);
};

export default EngagementWeekCalendar;